import type { ActivityKind, ActivityView, ResultEffect, TagColor } from '@erp/shared';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';

import { api } from './api.ts';

// Types mirror the API's responses (apps/api/src/activities/activities.types.ts).

export interface ActivityItem {
  id: string;
  title: string;
  type: { id: string; name: string; kind: ActivityKind; color: TagColor };
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  status: 'pending' | 'done';
  isPrivate: boolean;
  overdue: boolean;
  deal: { id: string; title: string; visible: boolean } | null;
  contact: { id: string; name: string | null; visible: boolean } | null;
  property: { id: string; code: string; title: string; visible: boolean } | null;
  note: string | null;
  result: { id: string; name: string; effect: ResultEffect } | null;
  resultComment: string | null;
  completedAt: string | null;
  reminderMinutes: number | null;
  assignedTo: { membershipId: string; name: string };
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  can: { edit: boolean; delete: boolean };
}

export interface BusyItem {
  busy: true;
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  assignedTo: { membershipId: string; name: string };
  overlap: boolean;
}

export type CalendarItem = (ActivityItem & { busy: false; overlap: boolean }) | BusyItem;

export interface ConflictItem {
  id: string | null;
  title: string;
  startsAt: string;
  endsAt: string | null;
}

export interface NextActivitySuggestion {
  reason: 'second_visit' | 'next_step';
  typeId: string | null;
  title: string | null;
  dealId: string | null;
  contactId: string | null;
  propertyId: string | null;
}

export interface ActivityType {
  id: string;
  kind: ActivityKind;
  name: string;
  color: TagColor;
  defaultReminderMinutes: number | null;
  active: boolean;
  results: { id: string; name: string; effect: ResultEffect; active: boolean }[];
}

export type RecordLink = 'deal' | 'contact' | 'property';

export interface ActivityFilters {
  view: ActivityView;
  typeId?: string | undefined;
  assignedMembershipId?: string | undefined;
  page?: number;
}

export const activityKeys = {
  all: ['activities'] as const,
  list: (filters: ActivityFilters) => ['activities', 'list', filters] as const,
  calendar: (from: string, to: string, members: string[]) =>
    ['activities', 'calendar', from, to, members] as const,
  detail: (id: string) => ['activities', 'detail', id] as const,
  record: (link: RecordLink, id: string) => ['activities', 'record', link, id] as const,
  conflicts: (params: string) => ['activities', 'conflicts', params] as const,
  trash: ['activities', 'trash'] as const,
  types: (all: boolean) => ['activity-types', all] as const,
};

function toQuery(filters: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

export const useActivities = (filters: ActivityFilters) =>
  useQuery({
    queryKey: activityKeys.list(filters),
    queryFn: () =>
      api<{ total: number; items: ActivityItem[] }>(
        `/activities${toQuery({ pageSize: 50, ...filters })}`,
      ),
    placeholderData: keepPreviousData,
  });

export const useCalendar = (from: string, to: string, members: string[]) =>
  useQuery({
    queryKey: activityKeys.calendar(from, to, members),
    queryFn: () =>
      api<CalendarItem[]>(
        `/activities/calendar${toQuery({ from, to, members: members.join(',') })}`,
      ),
    placeholderData: keepPreviousData,
  });

export const useActivity = (id: string | null) =>
  useQuery({
    queryKey: activityKeys.detail(id ?? ''),
    queryFn: () => api<ActivityItem>(`/activities/${id ?? ''}`),
    enabled: Boolean(id),
    retry: false,
  });

const RECORD_PATH: Record<RecordLink, string> = {
  deal: 'deals',
  contact: 'contacts',
  property: 'properties',
};

export const useRecordActivities = (link: RecordLink, id: string) =>
  useQuery({
    queryKey: activityKeys.record(link, id),
    queryFn: () => api<ActivityItem[]>(`/${RECORD_PATH[link]}/${id}/activities`),
  });

export const useActivityTypes = (all = false) =>
  useQuery({
    queryKey: activityKeys.types(all),
    queryFn: () => api<ActivityType[]>(`/activity-types${all ? '?all=true' : ''}`),
    staleTime: 5 * 60_000,
  });

/** Pending activities of the member that overlap the time (a warning; saving is allowed). */
export const useConflicts = (
  params: {
    startsAt: string;
    endsAt?: string | undefined;
    assignedMembershipId?: string | undefined;
    excludeId?: string | undefined;
  } | null,
) => {
  const query = params ? toQuery(params) : '';
  return useQuery({
    queryKey: activityKeys.conflicts(query),
    queryFn: () => api<ConflictItem[]>(`/activities/conflicts${query}`),
    enabled: params !== null,
    staleTime: 10_000,
  });
};

/** After any change: lists, calendar, record cards and the deals (next activity). */
export async function refreshActivities(client: QueryClient): Promise<void> {
  await Promise.all([
    client.invalidateQueries({ queryKey: activityKeys.all }),
    client.invalidateQueries({ queryKey: ['deals'] }),
  ]);
}

// ---------------------------------------------------------------------------
// Dates in the agency's time zone
// ---------------------------------------------------------------------------

/** "2026-09-28" and "14:30" of an instant, in `timeZone`. */
export function zonedParts(value: Date | string, timeZone: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(value));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  };
}

/** The instant (ISO) of a local date and time in `timeZone`. */
export function zonedToIso(date: string, time: string, timeZone: string): string {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  const [h = 0, min = 0] = time.split(':').map(Number);
  const guess = Date.UTC(y, m - 1, d, h, min);
  // The zone's offset at that moment (El Salvador has no daylight saving time).
  const local = zonedParts(new Date(guess), timeZone);
  const [ly = 0, lm = 1, ld = 1] = local.date.split('-').map(Number);
  const [lh = 0, lmin = 0] = local.time.split(':').map(Number);
  const offset = Date.UTC(ly, lm - 1, ld, lh, lmin) - guess;
  return new Date(guess - offset).toISOString();
}

/** Adds days to a "YYYY-MM-DD" date. */
export function addDays(date: string, days: number): string {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** 0 = Monday ... 6 = Sunday. */
export function weekday(date: string): number {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

export function todayIn(timeZone: string): string {
  return zonedParts(new Date(), timeZone).date;
}

const DAY_NAMES = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** "lunes 28" / "lunes 28 de septiembre". */
export function dayLabel(date: string, withMonth = false): string {
  const [, m = 1, d = 1] = date.split('-').map(Number);
  const name = DAY_NAMES[weekday(date)] ?? '';
  return withMonth ? `${name} ${String(d)} de ${MONTH_NAMES[m - 1] ?? ''}` : `${name} ${String(d)}`;
}

/** "septiembre de 2026". */
export function monthLabel(date: string): string {
  const [y = 0, m = 1] = date.split('-').map(Number);
  return `${MONTH_NAMES[m - 1] ?? ''} de ${String(y)}`;
}

export const REMINDER_OPTIONS: [number | null, string][] = [
  [null, 'Sin recordatorio'],
  [0, 'A la hora'],
  [5, '5 minutos antes'],
  [10, '10 minutos antes'],
  [15, '15 minutos antes'],
  [30, '30 minutos antes'],
  [60, '1 hora antes'],
  [120, '2 horas antes'],
  [1440, '1 día antes'],
];
